import { Button, Tooltip } from "@mui/material";
import { translate } from "../../utils/translate";
import styles from "../../css/modules/departmentOverview.module.scss";
import { useCallback, useState, useEffect } from "react";
import { Popup } from "@bryntum/gantt";
import { updateSettings } from "../../utils/updateSettings";
import { settingActions } from "../../redux/setting";
import { useDispatch } from "react-redux";
import { getColumnId } from "../../pages/Settings/components/Columns/configs";
import { useApiClient } from "../ApiProvider";
import authenticationManager from "../../utils/authenticationManager";

const settingsCollectors = [
  ({ gantt, histogram, scheduler, utilization, configs }) => {
    const element = [gantt, histogram, scheduler, utilization].find((i) => i && !i.hidden);
    if (!element) {
      return [];
    }
    return {
      key: "taskGridWidth",
      value: Math.round((element.subGrids.locked.width * 100) / element.element.offsetWidth),
      oldValue: configs.taskGridWidth,
    };
  },
  ({ gantt, configs }) => {
    if (configs.showGantt === undefined) {
      return [];
    }
    return {
      key: "showGantt",
      value: !!gantt?.isVisible,
      oldValue: configs.showGantt,
    };
  },
  ({ scheduler, configs }) => {
    if (!scheduler) {
      return [];
    }
    return {
      key: "showScheduler",
      value: !!scheduler?.isVisible,
      oldValue: configs.showScheduler,
    };
  },
  ({ histogram, configs }) => {
    return {
      key: "showHistogram",
      value: !!histogram?.isVisible,
      oldValue: configs.showHistogram,
    };
  },
  ({ utilization, configs }) => {
    if (configs.showUtilization === undefined) {
      return [];
    }
    return {
      key: "showUtilization",
      value: !!utilization?.isVisible,
      oldValue: !!configs.showUtilization,
    };
  },
  ({ gantt, configs }) => {
    return {
      key: "hideWeekends",
      value: gantt.workingTime.fromDay === 1,
      oldValue: configs.hideWeekends === true,
    };
  },
  ({ gantt, configs }) => {
    return [
      { key: "indicatorEarlyDates", configKey: "earlyDates" },
      { key: "indicatorConstraintDate", configKey: "constraintDate" },
      { key: "indicatorLateDates", configKey: "lateDates" },
      { key: "indicatorDeadlineDate", configKey: "deadlineDate" },
    ].map(({ key, configKey }) => ({
      key: key,
      text: translate("settingsFieldLabel_" + key),
      value: !!gantt.features.indicators.items[configKey],
      oldValue: configs[key],
    }));
  },
  ({ gantt, configs }) => {
    const configColumns = JSON.parse(JSON.stringify(configs?.columns || {}));

    let withChanged = false;
    let orderChanged = false;
    let visibilityChanged = false;

    gantt.columns.forEach((item) => {
      const key = getColumnId(item.data);
      const conf = configColumns[key];
      if (conf) {
        const oldOrder = typeof conf.order !== "number" ? -1 : conf.order;
        const order = item.data.order;
        const oldWidth = conf.width;
        const width = item.width;
        const oldHidden = conf.hidden;
        const hidden = item.hidden;
        if (width !== oldWidth) {
          withChanged = true;
          conf.width = width;
        }
        if (order !== oldOrder) {
          orderChanged = true;
          conf.order = order;
        }
        if (oldHidden !== hidden) {
          visibilityChanged = true;
          conf.hidden = hidden;
        }
      }
    });
    const res = {
      key: "columns",
      value: configColumns,
      text: translate("Column settings"),
      hasChanges: withChanged || orderChanged || visibilityChanged,
    };
    if (withChanged || orderChanged || visibilityChanged) {
      res.container = {};
      if (withChanged) {
        res.container.from = {
          html: translate(`Columns width changed`),
        };
      }
      if (orderChanged) {
        res.container.to = {
          html: translate(`Columns order changed`),
        };
      }
      if (visibilityChanged) {
        res.container.to = {
          html: translate(`Columns visibility changed`),
        };
      }
    }
    return res;
  },
];

export function SaveSettings({ configs, api, groupKey }) {
  const dispatch = useDispatch();
  const [token, setToken] = useState(null);
  const apiClient = useApiClient();

  useEffect(() => {
    if (window['auth0Config'].USE_AUTH_0) {
      const retrieveToken = async () => {
        const token = await apiClient.getAccessToken();
        setToken(token);
      };
      retrieveToken();
    } else {
      //jwt token from settings
      const token = authenticationManager.getAccessToken();
      setToken(token);
    }
  }, [apiClient]);

  const onSave = useCallback(() => {
    const gantt = api.gantt;
    const histogram = api.histogram;
    const scheduler = api.scheduler;
    const utilization = api.utilization;
    const items = settingsCollectors
      .map((i) => i({ gantt, histogram, scheduler, utilization, configs }))
      .flat()
      .map((item) => {
        const hasChanges = item.hasChanges === undefined ? item.value !== item.oldValue : item.hasChanges;
        let container = item.container;
        if (hasChanges && !container) {
          container = {
            from: {
              html: `From - &nbsp;<b class="b">${item.oldValue}</b>`,
            },
            to: {
              html: `To - &nbsp;<b class="b">${item.value} </b>`,
            },
          };
        }
        return {
          cls: "custom-checkbox",
          checked: hasChanges,
          disabled: !hasChanges,
          id: item.key,
          type: "checkbox",
          dataValue: item.value,
          text: item.text || translate("settingsFieldLabel_" + item.key),
          container,
        };
      })
      .sort((a, b) => Number(b.checked) - Number(a.checked));
    const canSave = items.find((i) => i.checked);
    const popup = new Popup({
      width: 500,
      maxHeight: "80vh",
      title: translate("Save settings"),
      modal: {
        closeOnMaskTap: true,
      },
      centered: true,
      scrollable: true,
      closeAction: "destroy",
      closable: true,
      items: items,
      bbar: [
        {
          type: "button",
          text: "Cancel",
          style: "min-width: 30%",
          color: "b-raised",
          onClick: () => {
            popup.close();
          },
        },
        {
          type: "button",
          text: "Save",
          disabled: !canSave,
          ref: "saveButton",
          style: "min-width: 30%",
          color: "b-raised",
          onClick: async () => {
            const saveProps = {};
            popup.items.forEach((i) => {
              if (i.checked) {
                saveProps[i.id] = i.dataValue;
              }
            });
            await updateSettings(groupKey, saveProps, true, apiClient);
            dispatch(settingActions.fetchSetting(token));
            popup.close();
          },
        },
      ],
    });
  }, [api, configs, dispatch, groupKey, token]);

  return (
    <Tooltip title={translate("Save settings")}>
      <Button onClick={onSave} size="small" className={styles.iconBtn} data-testid="tb_depview_savesettings">
        <span className="ico icon-cog" />
      </Button>
    </Tooltip>
  );
}
